import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Dropdown, DropdownContent } from '../../../ui';
import { cn } from '../../../../lib/utils';
import { SELECT_OPTION_COLORS, SelectOption } from '../types/select-options';
import { COLOR_MAPPINGS } from '../../../../utils/colorUtils';
import { createPortal } from 'react-dom';

interface SelectOptionColorPickerProps {
  option: SelectOption;
  onColorSelect: (color: string) => void;
  className?: string;
  position?: 'bottom' | 'top' | 'right'; // Control dropdown position
  targetId?: string; // For external event-based opening
  isMobile?: boolean; // Mobile styling adjustments
}

/**
 * A color picker component for select options
 */
export function SelectOptionColorPicker({
  option,
  onColorSelect,
  className = '',
  position = 'bottom',
  targetId,
  isMobile = false,
}: SelectOptionColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

  // Handle external open event - for color pickers that are in portals
  useEffect(() => {
    if (!targetId) return;

    const handleExternalOpen = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.id === targetId) {
        setIsOpen(true);

        // Position the picker in the appropriate location
        setTimeout(() => positionPicker(), 0);
      }
    };

    // Listen for both desktop and mobile events
    document.addEventListener('open-color-picker', handleExternalOpen);
    document.addEventListener('open-color-picker-mobile', handleExternalOpen);

    return () => {
      document.removeEventListener('open-color-picker', handleExternalOpen);
      document.removeEventListener('open-color-picker-mobile', handleExternalOpen);
    };
  }, [targetId]);

  // Handle clicks outside for external picker
  useEffect(() => {
    if (!isOpen || !targetId) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, targetId]);

  // Function to position the picker when it's external (in a portal)
  const positionPicker = () => {
    if (!targetId) return;

    // Find the element by its color and ID attributes
    const targetElements = document.querySelectorAll(`[data-color-target="${targetId}"]`);
    if (targetElements.length === 0) return;

    const targetElement = targetElements[0];
    const rect = targetElement.getBoundingClientRect();

    // Position to the right
    if (position === 'right') {
      setPickerPosition({
        top: rect.top + window.scrollY,
        left: rect.right + window.scrollX + 5, // 5px spacing
      });
    }
    // Position on top
    else if (position === 'top') {
      setPickerPosition({
        top: rect.top + window.scrollY - 220, // height of picker + spacing
        left: rect.left + window.scrollX - 100 + rect.width / 2, // center align
      });
    }
    // Position on bottom (default)
    else {
      setPickerPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX - 100 + rect.width / 2, // center align
      });
    }
  };

  // External color picker (rendered in a portal)
  if (targetId) {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={colorPickerRef}
        className="fixed z-[9999] bg-white shadow-lg rounded-md p-2 w-[220px]"
        style={{ top: `${pickerPosition.top}px`, left: `${pickerPosition.left}px` }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-wrap gap-1 p-1">
          {SELECT_OPTION_COLORS.map(color => (
            <button
              key={color}
              onClick={() => {
                onColorSelect(color);
              }}
              className={cn(
                isMobile ? 'w-8 h-8' : 'w-6 h-6',
                'rounded-full flex items-center justify-center',
                COLOR_MAPPINGS.dot[color], // Use dot colors (more vibrant) instead of bg colors (pastel)
                option.color === color && 'ring-2 ring-blue-500'
              )}
              title={`${color.charAt(0).toUpperCase() + color.slice(1)} color`}
            >
              {option.color === color && (
                <Check size={isMobile ? 14 : 12} className="text-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>,
      document.body
    );
  }

  // Standard inline color picker (not in a portal)
  return (
    <div className={cn('inline-block relative', className)}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-5 h-5 rounded-full transition-colors flex items-center justify-center',
          COLOR_MAPPINGS.bg[option.color] || 'bg-gray-200',
          'hover:opacity-80'
        )}
        title="Change color"
      ></button>

      <Dropdown
        trigger={<span style={{ display: 'none' }} />}
        open={isOpen}
        onOpenChange={setIsOpen}
        width={200}
        position={position === 'top' ? 'top-left' : 'bottom-left'}
        anchorElement={buttonRef.current}
        contentClassName="shadow-lg rounded-md p-2"
      >
        <DropdownContent>
          <div className="flex flex-wrap gap-1 p-1">
            {SELECT_OPTION_COLORS.map(color => (
              <button
                key={color}
                onClick={() => {
                  onColorSelect(color);
                  setIsOpen(false);
                }}
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center',
                  COLOR_MAPPINGS.dot[color], // Use dot colors (more vibrant) instead of bg colors (pastel)
                  option.color === color && 'ring-2 ring-blue-500'
                )}
                title={`${color.charAt(0).toUpperCase() + color.slice(1)} color`}
              >
                {option.color === color && <Check size={12} className="text-blue-600" />}
              </button>
            ))}
          </div>
        </DropdownContent>
      </Dropdown>
    </div>
  );
}
